import { ComponentPropsWithoutRef, forwardRef } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { useAuth } from 'hooks/useAuth';
import { useService } from 'hooks/useService';
import { ContentType } from 'services/abstract';
import { cn } from 'lib/utils';

type AccountAvatarProps = Omit<
  ComponentPropsWithoutRef<'button'>,
  'children'
> & {
  format?: 'thumbnail' | 'small' | 'medium' | 'large';
  avatarUrl?: string;
};

export const AccountAvatar = forwardRef<HTMLButtonElement, AccountAvatarProps>(
  ({ format = 'thumbnail', avatarUrl, className, ...buttonProps }, ref) => {
    const { user } = useAuth();
    const userService = useService(ContentType.USERS);
    const avatarSizeClass = format === 'thumbnail' ? 'h-10 w-10' : 'h-16 w-16';

    if (!user) {
      return null;
    }

    const imageSrc =
      avatarUrl ?? userService.getAvatarUrl(user.avatar, 'thumbnail');
    const fallbackInitials = user.username
      ? user.username.slice(0, 2).toUpperCase()
      : 'GM';

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          'flex items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300',
          className
        )}
        {...buttonProps}
      >
        <Avatar className={avatarSizeClass}>
          <AvatarImage
            src={imageSrc}
            alt={user.avatar?.name ?? user.username}
          />
          <AvatarFallback>{fallbackInitials}</AvatarFallback>
        </Avatar>
      </button>
    );
  }
);

AccountAvatar.displayName = 'AccountAvatar';
